import * as yup from 'yup';
import { requiredMessage, validMessageForName } from '../utils';
import { ALPHABETS_REGEX } from '../constants';
import { ALPHABETS_REGEX_VALIDATION, INVALID_EMAIL, PASSWORD_IS_INVALID, PASSWORD_REGEX, PASSWORD_VALIDATION_MESSAGE } from '../../../../shared/lib/constants';
import { PhoneType } from '../../generated';

const emailValidationSchema = {
  email: yup
    .string()
    .email(INVALID_EMAIL)
    .matches(/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/, INVALID_EMAIL)
    .required(requiredMessage('Email'))
};

const firstNameValidationSchema = {
  firstName: yup.string().matches(ALPHABETS_REGEX, validMessageForName(ALPHABETS_REGEX_VALIDATION)).required(requiredMessage('First Name'))
};

const zipCodeValidationSchema = {
  address_zip: yup.string().matches(/^[0-9]*$/, 'Zip code must only contain numeric values.')
};

const lastNameValidationSchema = {
  lastName: yup.string().matches(ALPHABETS_REGEX, validMessageForName(ALPHABETS_REGEX_VALIDATION)).required(requiredMessage('Last Name'))
};

const passwordValidationSchema = {
  password: yup.string().required(requiredMessage('Password')).matches(PASSWORD_REGEX, PASSWORD_VALIDATION_MESSAGE)
};

const stateValidationSchema = {
  address_state: yup.string().required(requiredMessage('State')).matches(ALPHABETS_REGEX, 'Only characters are allowed.')
};

const cityValidationSchema = {
  address_city: yup
    .string()
    .required(requiredMessage('City'))
    .matches(/^[a-zA-Z\s'-]+$/, 'City should only contain letters, spaces, apostrophes, and hyphens.')
};

export const resetPasswordSchema = yup.object({
  newPassword: yup.string().required(requiredMessage('Password')).matches(PASSWORD_REGEX, PASSWORD_VALIDATION_MESSAGE),
  repeatPassword: yup
    .string()
    .oneOf([yup.ref('newPassword'), undefined], 'Passwords must match.')
    .required('Please confirm your password.')
});

export const loginValidationSchema = yup.object({
  ...emailValidationSchema,
  password: yup.string().required(requiredMessage('Password')).matches(PASSWORD_REGEX, PASSWORD_IS_INVALID)
});

export const cardValidationScehma = yup.object({
  name: yup.string().required(),
  expiry: yup.string().default(''),
  cvc: yup.string().default(''),
  number: yup.string().default('')
});

export const billingValidationSchema = (validateCard: boolean) =>
  yup.object({
    coupon: yup.string(),
    price: yup.string(),
    cardDetail: validateCard
      ? yup.object().shape({
          ...firstNameValidationSchema,

          ...cityValidationSchema,

          lastName: yup.string().notRequired().default(''),
          country: yup.string().nonNullable().required(requiredMessage('Country')),
          address_line1: yup.string().required(requiredMessage('Street Address Line 1')),
          address_line2: yup.string(),
          ...stateValidationSchema,
          ...zipCodeValidationSchema,
          expiry: yup.string(),
          cvc: yup.string(),
          number: yup.string()
        })
      : yup.mixed().notRequired()
  });

export const signUpValidationSchema = yup.object({
  ...firstNameValidationSchema,
  ...lastNameValidationSchema,
  ...emailValidationSchema,
  ...passwordValidationSchema,
  passwordAgain: yup
    .string()
    .oneOf([yup.ref('password'), undefined], 'Passwords must match.')
    .required('Please confirm your password.')
});

export const forgotPasswordValidationSchema = yup.object({
  ...emailValidationSchema
});

const phoneNumberValidationSchema = {
  phoneNumber: yup.string().required(requiredMessage('Phone Number'))
};

export const addPhoneNumberValidationSchema = yup.object({
  ...phoneNumberValidationSchema
});

export const editPhoneNumberValidationSchema = yup.object({
  ...phoneNumberValidationSchema
});

export const createContactValidationSchema = yup.object().shape(
  {
    nickName: yup.string().required(requiredMessage('Nickname')),
    name: yup.string().required(requiredMessage('Name')),
    phoneNumber: yup.string().required(requiredMessage('Phone Number')),
    // email: yup.string().nullable().email(INVALID_EMAIL)

    email: yup
      .string()
      .when('phoneType', (val) => {
        if (val[0] == PhoneType.LandLine) {
          return yup.string().email(INVALID_EMAIL); //.required(requiredMessage("Email")); //REVERTED PENDING CHANGE REQUEST
        } else {
          return yup.string().notRequired().email(INVALID_EMAIL);
        }
      })
      .nullable()
      .optional()
  },
  [
    // Cyclic dependency for durationMonth
    ['email', 'email']
  ]
);

export const editContactValidationSchema = yup.object().shape(
  {
    nickName: yup.string().required(requiredMessage('Nickname')),
    name: yup.string().required(requiredMessage('Name')),
    ...phoneNumberValidationSchema,

    email: yup
      .string()
      .when('phoneType', (val) => {
        if (val[0] == PhoneType.LandLine) {
          return yup.string().email(INVALID_EMAIL); //.required(requiredMessage("Email")); //REVERTED PENDING CHANGE REQUEST
        } else {
          return yup.string().notRequired().email(INVALID_EMAIL);
        }
      })
      .nullable()
      .optional()
  },
  [
    // Cyclic dependency for durationMonth
    ['email', 'email']
  ]
);

export const editAccountName = yup.object({
  ...firstNameValidationSchema,
  ...lastNameValidationSchema
});

export const editAccountEmailValidationSchema = (currentEmail: string) =>
  yup.object({
    email: yup
      .string()
      .email(INVALID_EMAIL)
      .matches(/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/, INVALID_EMAIL)
      .required(requiredMessage('Email'))
      .test('not-same-email', 'New email must be different from your current email.', (value) =>
        !value || value.toLowerCase().trim() !== currentEmail.toLowerCase().trim()
      ),
    confirmEmail: yup
      .string()
      .oneOf([yup.ref('email'), undefined], 'Emails must match.')
      .required('Please confirm your email.')
  });

export const changeCardValidationSchema = yup.object({
  name: yup.string(),
  expiry: yup
    .string()
    .required(requiredMessage('Expiration'))
    .matches(/^(0[1-9]|1[0-2])\/(2[3-9]|[3-9]\d)$/, 'Invalid expiration date.'),
  cvc: yup
    .string()
    .required(requiredMessage('CVC'))
    .matches(/^\d{3}$/, 'CVC must be exactly 3 numeric digits.'),
  number: yup
    .string()
    .required(requiredMessage('Card Number'))
    .matches(/^\d{16}$/, 'Card number must be exactly 16 digits.')
});

export const editBillingInfoValidationSchema = yup.object({
  ...cityValidationSchema,
  address_country: yup.string().nonNullable().required(requiredMessage('Country')),
  address_line1: yup.string().required(requiredMessage('Street Address Line 1')),
  address_line2: yup.string(),
  ...stateValidationSchema,
  ...zipCodeValidationSchema
});

export const stripePaymentSchema = yup.object({
  name: yup.string().required('Name is required.').max(20, 'Name should be less than 20 characters.'),
  card: yup.string().default(''),
  cvc: yup.string().default(''),
  expiry: yup.string().default('')
});
